<template>
  <b-card v-if="churn" no-body class="earnings-card">
    <b-card-header>
      <h4 class="mb-0">Churn</h4>
      <b-card-text class="mb-0 totalizer-help">
        <b-button id="help-churn-totalizer" variant="flat" class="btn-icon p-0">
          <feather-icon
            icon="HelpCircleIcon"
            size="21"
            class="text-muted cursor-pointer"
          />
        </b-button>
        <b-popover
          target="help-churn-totalizer"
          triggers="focus"
          placement="top"
        >
          <template #title>Churn</template>
          <p class="mb-50">
            Taxa de cancelamento = cancelamentos ÷ (base + cancelamentos).
          </p>
          <p class="mb-25">
            <b>Contam como cancelamento:</b> Nunca ativo, Cancelado após rodando.
          </p>
          <p class="mb-50">
            <b>Compõem a base:</b> Proposta implantada, Inadimplente,
            Inadimplente após rodando, Completo, Rodando.
          </p>
          <p class="mb-0 text-muted">
            O indicador respeita o filtro de Status: ao selecionar status, ele
            considera apenas a interseção com os status acima.
          </p>
        </b-popover>
      </b-card-text>
    </b-card-header>
    <b-card-body>
      <!--
        Rosca só é desenhada com denominador maior que zero: série toda zero faz o
        ApexCharts renderizar NaN. Sem dado o corpo fica vazio, igual aos cards de
        status, que escondem o gráfico quando não há quantidade.
      -->
      <template v-if="hasData">
        <div>
          <vue-apex-charts
            height="130"
            :options="chartOptions"
            :series="series"
          />
        </div>
      </template>
      <div v-if="loading" class="position-absolute spinner-position">
        <b-spinner variant="primary" />
      </div>
    </b-card-body>
    <b-row class="text-center mx-0">
      <b-col
        cols="6"
        class="border-top border-right d-flex align-items-between flex-column py-1"
        style="padding-left: 1px; padding-right: 1px"
      >
        <b-card-text class="text-muted mb-0" style="font-size: smaller">
          Total de contratos
        </b-card-text>
        <div class="font-weight-bolder mb-0">
          {{ churn.total_quantity }}
        </div>
      </b-col>
      <b-col
        cols="6"
        class="border-top d-flex align-items-between flex-column py-1"
      >
        <b-card-text class="text-muted mb-0" style="font-size: smaller">
          Cancelamentos
        </b-card-text>
        <div class="font-weight-bolder mb-0">
          {{ churn.canceled_quantity }}
        </div>
      </b-col>
    </b-row>
    <div v-if="hasNoBase" class="px-1 pb-1">
      <small class="text-warning">
        <feather-icon icon="AlertTriangleIcon" size="13" class="mr-25" />
        O filtro de Status atual não inclui nenhum status da base do churn.
      </small>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BCardText,
  BRow,
  BCol,
  BButton,
  BPopover,
  BSpinner,
} from "bootstrap-vue";
import VueApexCharts from "vue-apexcharts";

// Mesmos tons neutros do ContractsTotalizer, para as duas roscas da tela
// combinarem no claro e no escuro.
const REMAINDER_COLOR_DARK = "#4b5a7e";
const REMAINDER_COLOR_LIGHT = "#e2e8f0";
// $danger do tema (src/@core/scss/base/bootstrap-extended/_variables.scss).
const CANCELED_COLOR = "#ea5455";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BCardText,
    BRow,
    BCol,
    BButton,
    BPopover,
    BSpinner,
    VueApexCharts,
  },
  props: {
    churn: {
      type: Object,
      default: undefined,
    },
    // Mesmo spinner dos cards de status, alimentado pelo mesmo `loading` da
    // tela, para os cards da grade acenderem e apagarem juntos.
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    canceledQuantity() {
      return Number(this.churn?.canceled_quantity) || 0;
    },
    baseQuantity() {
      return Number(this.churn?.base_quantity) || 0;
    },
    // Denominador da fórmula. As duas fatias da rosca somam exatamente este
    // valor, então o arco vermelho já representa a taxa sem recálculo.
    hasData() {
      return this.canceledQuantity + this.baseQuantity > 0;
    },
    series() {
      return [this.canceledQuantity, this.baseQuantity];
    },
    formattedRate() {
      const rate = Number(this.churn?.rate) || 0;
      return `${rate.toFixed(2).replace(".", ",")}%`;
    },
    isDarkMode() {
      return this.$store.state.appConfig?.layout?.skin === "dark";
    },
    // Base vazia com cancelamentos presentes joga a taxa para 100% e engana a
    // leitura; melhor avisar que o recorte de status é que está estreito.
    hasNoBase() {
      return (this.churn?.base_statuses || []).length === 0;
    },
    chartOptions() {
      // O percentual central vem do `rate` calculado no backend, não de uma
      // soma feita aqui, para não haver duas fontes para o mesmo número.
      const rateLabel = this.formattedRate;
      // Denominador da fórmula, usado só para converter a fatia sob o cursor em
      // percentual. Os cards de status nunca mostram número cru no centro, e a
      // série deles já é percentual; aqui a série é quantidade, então a
      // conversão acontece no formatter.
      const denominator = this.canceledQuantity + this.baseQuantity;
      const toPercentLabel = (quantity) => {
        const value = Number(quantity) || 0;
        const percent = denominator > 0 ? (value / denominator) * 100 : 0;
        return `${percent.toFixed(2).replace(".", ",")}%`;
      };

      return {
        chart: {
          type: "donut",
          toolbar: { show: false },
          width: "100%",
        },
        dataLabels: { enabled: false },
        legend: { show: false },
        labels: ["Cancelamentos", "Base"],
        colors: [
          CANCELED_COLOR,
          this.isDarkMode ? REMAINDER_COLOR_DARK : REMAINDER_COLOR_LIGHT,
        ],
        stroke: { width: 0 },
        grid: {
          padding: { right: -20, bottom: -8, left: -20 },
        },
        tooltip: {
          x: { show: false },
          y: {
            formatter(value) {
              return Number(value) || 0;
            },
          },
        },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                // Rótulo fixo em "Total" e valor sempre em percentual, mesmo com
                // o cursor sobre uma fatia — igual aos cards de status. A
                // quantidade da fatia continua disponível no tooltip.
                name: {
                  offsetY: 15,
                  formatter() {
                    return "Total";
                  },
                },
                value: {
                  offsetY: -15,
                  formatter(val) {
                    return toPercentLabel(val);
                  },
                },
                total: {
                  show: true,
                  offsetY: 15,
                  label: "Total",
                  formatter() {
                    return rateLabel;
                  },
                },
              },
            },
          },
        },
      };
    },
  },
};
</script>
